// Exports PDF au format A4 : tableau d'effectif (paysage, colonnes réparties sur plusieurs pages)
// et fiches individuelles (portrait, une page par enfant).
const LOGO = "/img/logo-usc.png";
export const TITRE = "Effectif USC Jemappes U12 C";
export const CONFIDENTIEL = "Document confidentiel – usage interne au club. Ne pas diffuser.";
const NAVY = [10, 22, 40];
const GOLD = [212, 175, 55];
const STAFF_BG = [253, 240, 196];

const logoData = async () => {
  const blob = await (await fetch(LOGO)).blob();
  return new Promise((ok, ko) => { const r = new FileReader(); r.onload = () => ok(r.result); r.onerror = ko; r.readAsDataURL(blob); });
};

const libs = async () => {
  const { jsPDF } = await import("jspdf");
  const { default: autoTable } = await import("jspdf-autotable");
  let logo = null;
  try { logo = await logoData(); } catch { /* export sans logo */ }
  return { jsPDF, autoTable, logo };
};

const entete = (doc, logo, titre, sousTitre) => {
  if (logo) doc.addImage(logo, "PNG", 10, 7, 13, 14.2);
  doc.setFontSize(15);
  doc.setTextColor(0, 0, 0);
  doc.text(titre, 27, 13);
  doc.setFontSize(7.5);
  doc.setTextColor(200, 30, 30);
  doc.text(sousTitre ?? CONFIDENTIEL, 27, 18.5);
  doc.setTextColor(0, 0, 0);
};

// Répartit les colonnes en groupes qui tiennent dans la largeur d'une page ; Nom et Prénom sont répétés sur chaque page.
export const decouper = (cols, largeurMax) => {
  const fixes = cols.filter(([l]) => l === "Nom" || l === "Prénom");
  const autres = cols.filter((c) => !fixes.includes(c));
  const base = fixes.reduce((n, [, , w]) => n + w, 0);
  const pages = [];
  let cur = [], w = base;
  autres.forEach((c) => {
    if (cur.length && w + c[2] > largeurMax) { pages.push(cur); cur = []; w = base; }
    cur.push(c); w += c[2];
  });
  if (cur.length || pages.length === 0) pages.push(cur);
  return pages.map((p) => cols.filter((c) => fixes.includes(c) || p.includes(c)));
};

const MM_PAR_CAR = 1.45;
const LARGEUR_A4_PAYSAGE = 277;

export const exportTableauA4 = async (list, cols, rowsFor, fichier) => {
  const { jsPDF, autoTable, logo } = await libs();
  const doc = new jsPDF({ orientation: "landscape", format: "a4" });
  const groupes = decouper(cols, LARGEUR_A4_PAYSAGE / MM_PAR_CAR);
  groupes.forEach((g, i) => {
    if (i > 0) doc.addPage();
    entete(doc, logo, groupes.length > 1 ? `${TITRE} (${i + 1}/${groupes.length})` : TITRE);
    const total = g.reduce((n, [, , w]) => n + w, 0);
    autoTable(doc, {
      startY: 25, margin: { left: 10, right: 10 }, tableWidth: LARGEUR_A4_PAYSAGE,
      head: [g.map(([l]) => l)], body: rowsFor(list, g),
      columnStyles: Object.fromEntries(g.map(([, , w], k) => [k, { cellWidth: (w / total) * LARGEUR_A4_PAYSAGE }])),
      headStyles: { fillColor: NAVY, textColor: GOLD, fontSize: 7 }, styles: { fontSize: 7, cellPadding: 1.2, overflow: "linebreak" },
      didParseCell: (d) => { if (d.section === "body" && list[d.row.index]?.staff) { d.cell.styles.fillColor = STAFF_BG; d.cell.styles.fontStyle = "bold"; } },
    });
  });
  doc.save(fichier);
};

// Une page A4 portrait par personne : rubriques de la fiche avec libellé / valeur.
export const exportFichesA4 = async (list, cols, sections, fichier) => {
  const { jsPDF, autoTable, logo } = await libs();
  const doc = new jsPDF({ orientation: "portrait", format: "a4" });
  const choisies = new Set(cols.map(([l]) => l));
  list.forEach((j, i) => {
    if (i > 0) doc.addPage();
    entete(doc, logo, `Fiche de renseignements – ${j.prenom ? `${j.prenom} ` : ""}${j.nom.toUpperCase()}`);
    const body = [];
    sections.forEach(([titre, labels]) => {
      const lignes = cols.filter(([l]) => labels.includes(l) && choisies.has(l));
      if (!lignes.length) return;
      body.push([{ content: titre.toUpperCase(), colSpan: 2, styles: { fillColor: NAVY, textColor: GOLD, fontStyle: "bold" } }]);
      lignes.forEach(([l, f]) => body.push([l, f(j) || "—"]));
    });
    autoTable(doc, {
      startY: 25, margin: { left: 12, right: 12 }, body, theme: "grid",
      columnStyles: { 0: { cellWidth: 52, fontStyle: "bold", textColor: [60, 60, 60] }, 1: { cellWidth: 134 } },
      styles: { fontSize: 8.5, cellPadding: 1.6, overflow: "linebreak" },
      rowPageBreak: "avoid",
    });
    doc.setFontSize(7);
    doc.setTextColor(120, 120, 120);
    doc.text(`${TITRE} – ${new Date().toLocaleDateString("fr-FR")}`, 12, 290);
    doc.setTextColor(0, 0, 0);
  });
  doc.save(fichier);
};
